import {
  List as MaterialList,
  ListItem as MaterialListItem,
  ListItemAvatar as MaterialListItemAvatar,
  ListItemAvatarProps as MaterialListItemAvatarProps,
  ListItemProps as MaterialListItemProps,
  ListItemText as MaterialListItemText,
  ListItemTextProps as MaterialListItemTextProps,
  ListItemTypeMap,
  ListProps as MaterialListProps,
  ListTypeMap,
  useTheme,
} from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import React from 'react';

import type { Theme } from '../../themes/themes';
import { getStyles } from './List.styles';

const DEFAULT_VARIANT = 'icon';
const DEFAULT_SIZE = 'medium';
const DEFAULT_DISABLEGUTTERS = true;

export type ListProps<D extends React.ElementType = ListTypeMap['defaultComponent']> = MaterialListProps<
  D,
  {
    variant?: 'pill' | 'icon' | 'number';
    size?: 'small' | 'medium';
  }
>;

export type ListComponent<P, D extends React.ElementType = ListTypeMap['defaultComponent']> = OverridableComponent<
  ListTypeMap<P, D>
>;

/**
 * The Top Info list
 *
 * @version [AIV]{version}[/AIV]
 * @author [SFP](https://gitlab.sfp.berlin/products/sparkasse.de/portals)
 * @see [https://styleguide.sparkasse.de/document/69#/ui-komponenten/top-info-box]
 */
export const List: ListComponent<ListProps> = ({
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
  children,
  ...props
}: ListProps) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);
  const styleVariations: ListProps['sx'] = Object.assign(
    {},
    props.sx,
    variant ? styles[`variant:${variant}`] : {},
    size ? styles[`size:${size}`] : {}
  );
  return (
    <MaterialList sx={styleVariations} component={variant === 'number' ? 'ol' : 'ul'} {...props}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement<ListProps>(child, { variant, size }) : child
      )}
    </MaterialList>
  );
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type ListItemProps<P = {}, D extends React.ElementType = 'li'> = MaterialListItemProps<
  D,
  {
    variant?: 'pill' | 'icon' | 'number';
    size?: 'small' | 'medium';
  } & P
>;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type ListItemComponent<P = {}, D extends React.ElementType = 'li'> = OverridableComponent<ListItemTypeMap<P, D>>;

export const ListItem: ListItemComponent<ListItemProps> = ({
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
  disableGutters = DEFAULT_DISABLEGUTTERS,
  children,
  ...props
}: ListItemProps) => {
  return (
    <MaterialListItem disableGutters={disableGutters} {...props}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement<ListProps>(child, { variant, size }) : child
      )}
    </MaterialListItem>
  );
};

export type ListItemTextProps = MaterialListItemTextProps & {
  variant?: 'pill' | 'icon' | 'number';
  size?: 'small' | 'medium';
};

export const ListItemText: React.FC<ListItemTextProps> = ({
  size = DEFAULT_SIZE,
  primaryTypographyProps,
  secondaryTypographyProps,
  ...props
}) => {
  return (
    <MaterialListItemText
      {...props}
      primaryTypographyProps={{ variant: 'h4', component: 'div', ...primaryTypographyProps }}
      secondaryTypographyProps={{
        variant: size === 'medium' ? 'body1' : 'body2',
        component: 'div',
        ...secondaryTypographyProps,
      }}
    />
  );
};

export const ListItemAvatar: React.FC<MaterialListItemAvatarProps> = (props: MaterialListItemAvatarProps) => {
  return <MaterialListItemAvatar {...props} />;
};
